/**
 * runModeRendering.ts — Run Mode Phase C (G-3 / BUG-2026-0141) pure rendering
 * helpers for continuous (24/7) bookings.
 *
 * Phase B made the DATA correct (continuous bookings really span weekends);
 * this module makes the RENDERING honest. All the per-mode opt-outs of the
 * weekend-veil / snap-to-working-day logic live here as pure functions so
 * they are vitest-testable without React:
 *
 *   - bookingLabelRange  — booking-card date labels stop snapping inward to
 *     working days for continuous bookings (a Saturday end is REAL).
 *   - buildWeekStripStates — the Sa–Fr mini strip renders covered weekend /
 *     holiday cells as 'run' (running 24/7) instead of greyed non-work.
 *   - continuousDaySet   — which ISO days on a machine row are covered by a
 *     continuous booking, so the grid's non-work veil can switch those cells
 *     from "Weekend (lab closed)" to "continuous test running".
 *
 * Attended bookings are byte-identical to before: every helper falls back to
 * the existing weekUtils working-day behavior when runMode !== 'continuous'.
 */

import {
  firstWorkingDay,
  lastWorkingDay,
  countWorkingDays,
  isNonWorkingDay,
  saturdayWeekStart,
} from './weekUtils';

// ---------------------------------------------------------------------------
// Mode predicate + shared copy
// ---------------------------------------------------------------------------

/** True when a booking/task run-mode value means continuous (24/7). */
export function isContinuousRun(mode: string | null | undefined): boolean {
  return mode === 'continuous';
}

/** Short bar affordance for continuous runs. */
export const CONTINUOUS_BADGE = '24/7';

/** One-line explanation used by bar tooltips (Machine Schedule + grids). */
export const CONTINUOUS_HINT =
  'Continuous test — runs around the clock incl. weekends; the technician is booked every working day of the run';

/**
 * One-line staffing-model explanation (RM-4/RM-5/RM-6 — pickers, drawers,
 * cells). Lab answer Q2 (2026-07-10): the technician is assigned for the
 * ENTIRE duration (full working-day span) — the pre-Q2 "setup + teardown only"
 * wording is retired.
 */
export const CONTINUOUS_STAFFING_HINT =
  'Continuous test — the technician is booked every working day from setup through teardown; the machine keeps running unstaffed on weekends and holidays';

// ---------------------------------------------------------------------------
// Date walking (ISO 'YYYY-MM-DD', UTC, no Date leakage)
// ---------------------------------------------------------------------------

function addDaysIso(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Inclusive CALENDAR days between two ISO dates (1 when equal; 0 if end < start). */
export function calendarDaysInclusive(startIso: string, endIso: string): number {
  if (!startIso || !endIso || endIso < startIso) return 0;
  const [sy, sm, sd] = startIso.split('-').map(Number);
  const [ey, em, ed] = endIso.split('-').map(Number);
  const ms = Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd);
  return Math.round(ms / 86_400_000) + 1;
}

// ---------------------------------------------------------------------------
// Booking-card date labels (Phase-2 MachineBar and friends)
// ---------------------------------------------------------------------------

export interface BookingLabelRange {
  /** First date the label should show. */
  labelStart: string;
  /** Last date the label should show. */
  labelEnd: string;
  /** Day count for the "Nd" figure. */
  days: number;
  /** "Nd" for attended, "Nd (24/7)" for continuous. */
  daysLabel: string;
}

/**
 * Per-mode booking label range.
 *
 * Attended (default): snap inward to the first/last WORKING day and count
 * working days — unchanged from the 2026-06-25 label-clamp behavior.
 *
 * Continuous: the raw span IS the truth (machine runs through weekends), so
 * no snapping, and the count is inclusive CALENDAR days tagged "(24/7)".
 */
export function bookingLabelRange(
  effectiveStart: string,
  effectiveEnd: string,
  holidays: string[] | undefined,
  runMode: string | null | undefined,
): BookingLabelRange {
  if (isContinuousRun(runMode)) {
    const days = calendarDaysInclusive(effectiveStart, effectiveEnd);
    return {
      labelStart: effectiveStart,
      labelEnd: effectiveEnd,
      days,
      daysLabel: `${days}d (${CONTINUOUS_BADGE})`,
    };
  }
  const labelStart = firstWorkingDay(effectiveStart, effectiveEnd, holidays);
  const labelEnd = lastWorkingDay(effectiveEnd, effectiveStart, holidays);
  const days = countWorkingDays(labelStart, labelEnd, holidays);
  return { labelStart, labelEnd, days, daysLabel: `${days}d` };
}

// ---------------------------------------------------------------------------
// Sa–Fr week strip states (BookingWeekStrip)
// ---------------------------------------------------------------------------

/**
 * One cell of the Sa–Fr strip:
 *   'off'     — weekday not covered by the booking span.
 *   'work'    — covered working day (solid fill).
 *   'nonwork' — covered weekend/stat-holiday on an ATTENDED booking (greyed).
 *   'run'     — covered weekend/stat-holiday on a CONTINUOUS booking — the
 *               specimen is running, so the cell reads as active, not closed.
 */
export type WeekStripDayState = 'off' | 'work' | 'nonwork' | 'run';

// BST week is Saturday→Friday. JS getUTCDay() → strip position.
const STRIP_POS: Record<number, number> = { 6: 0, 0: 1, 1: 2, 2: 3, 3: 4, 4: 5, 5: 6 };

/**
 * Compute the 7 Sa–Fr cell states for a booking span (pure; extracted from
 * BookingWeekStrip so the per-mode branch is unit-testable).
 *
 * A WORKING day wins for its weekday across multi-week spans (unchanged);
 * for continuous bookings the covered non-working days become 'run'.
 */
export function buildWeekStripStates(
  rangeStart: string,
  rangeEnd: string,
  holidays: string[] | undefined,
  runMode?: string | null,
): WeekStripDayState[] {
  const out: WeekStripDayState[] = ['off', 'off', 'off', 'off', 'off', 'off', 'off'];
  if (!rangeStart || !rangeEnd || rangeEnd < rangeStart) return out;
  const continuous = isContinuousRun(runMode);
  const holSet = holidays && holidays.length ? new Set(holidays) : null;
  const coveredNonWork: WeekStripDayState = continuous ? 'run' : 'nonwork';
  let cur = rangeStart;
  let guard = 0;
  // Walk up to ~6 weeks; a longer booking's weekday pattern repeats before that.
  while (cur <= rangeEnd && guard < 42) {
    const [y, m, d] = cur.split('-').map(Number);
    const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    const pos = STRIP_POS[dow];
    const nonWorking = dow === 0 || dow === 6 || (holSet?.has(cur) ?? false);
    if (!nonWorking) {
      // Continuous: the machine runs on working days too — but keep the
      // visual distinction identical to attended ('work') so only the
      // weekend/holiday cells change meaning.
      out[pos] = 'work';
    } else if (out[pos] === 'off') {
      out[pos] = coveredNonWork;
    }
    cur = addDaysIso(cur, 1);
    guard += 1;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Continuous staffing windows (RM-5/RM-6 — FE mirror of the backend's
// continuousStaffingWindows in backend/src/calculation/runModeContinuous.ts)
// ---------------------------------------------------------------------------

/** First WORKING day on/after `iso` (inclusive — mirror of backend nextWorkingDay). */
export function nextWorkingDayIso(iso: string, holidays?: readonly string[]): string {
  const set = holidays && holidays.length ? new Set(holidays) : undefined;
  let cur = iso;
  let guard = 0;
  while (isNonWorkingDay(cur, set) && guard < 62) {
    cur = addDaysIso(cur, 1);
    guard += 1;
  }
  return cur;
}

/** One dated attendance window (week row) of a continuous booking. */
export interface ContinuousWindow {
  /** BST Saturday of the window's week. */
  weekStart: string;
  startDate: string;
  endDate: string;
}

/**
 * The dated attendance windows of a CONTINUOUS booking — the days a tech is
 * actually claimed. Mirrors the backend contract exactly (lab answer Q2,
 * 2026-07-10: FULL-DURATION staffing — supersedes the setup+teardown-only
 * safe default), so picker availability / cell staffing cues agree with what
 * the save gate judges and the respan writes:
 *   - setup day = first working day >= bookingStart (RM-19);
 *   - teardown day = first working day >= bookingEnd (can fall OUTSIDE the
 *     booking span — a Saturday end tears down Monday, Amendment #5);
 *   - ONE window per BST week of [setupDay .. teardownDay], clamped to the
 *     span (first week starts at setup, last week ends at teardown, mid weeks
 *     span [Sat..Fri]) — the attended weekly fan's exact row shape. The
 *     person-day model marks WORKING days only, so weekends/holidays inside a
 *     window are never busy.
 */
export function continuousAttendanceWindows(
  bookingStart: string,
  bookingEnd: string,
  holidays?: readonly string[],
): ContinuousWindow[] {
  if (!bookingStart) return [];
  const setupDay = nextWorkingDayIso(bookingStart, holidays);
  const teardownDay = nextWorkingDayIso(
    bookingEnd && bookingEnd >= bookingStart ? bookingEnd : bookingStart,
    holidays,
  );
  // Degenerate/weekend-only booking: setup and teardown collapse to one day.
  const spanEnd = teardownDay >= setupDay ? teardownDay : setupDay;
  const out: ContinuousWindow[] = [];
  const lastWeek = saturdayWeekStart(spanEnd);
  let week = saturdayWeekStart(setupDay);
  let guard = 0;
  // Guard ~2 years of weeks; a corrupt range can't spin.
  while (week <= lastWeek && guard < 105) {
    const fri = addDaysIso(week, 6);
    out.push({
      weekStart: week,
      startDate: setupDay > week ? setupDay : week,
      endDate: spanEnd < fri ? spanEnd : fri,
    });
    week = addDaysIso(week, 7);
    guard += 1;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Machine-row continuous day coverage (MachineScheduleNonWorkOverlay opt-out)
// ---------------------------------------------------------------------------

/** Minimal booking shape the day-set builder needs. */
export interface ContinuousDaySetBooking {
  StartDate: string;
  EndDate: string;
  RunMode?: string | null;
}

/**
 * The set of ISO days covered by at least one CONTINUOUS booking, so the
 * grid's in-front non-work veil can render those weekend/holiday cells in the
 * "running (24/7)" state instead of "lab closed". Attended bookings never
 * contribute (their weekend cells stay veiled — the lab really is closed for
 * them). Spans are bounded (guard) so a corrupt range can't spin.
 */
export function continuousDaySet(
  bookings: ReadonlyArray<ContinuousDaySetBooking>,
): Set<string> {
  const days = new Set<string>();
  for (const b of bookings) {
    if (!isContinuousRun(b.RunMode)) continue;
    if (!b.StartDate || !b.EndDate || b.EndDate < b.StartDate) continue;
    let cur = b.StartDate;
    let guard = 0;
    while (cur <= b.EndDate && guard < 366) {
      days.add(cur);
      cur = addDaysIso(cur, 1);
      guard += 1;
    }
  }
  return days;
}
